import {
  ChargeConfiguration,
  Location,
  PlcCodes,
  Price,
  Product,
  TrialOption,
} from '@dch/products-business-logic';

export const MV_NSP_OPTION = 'KWKQJ';
export const FI_NSP_OPTION = 'PEJEG';
export const MI_NSP_OPTION = 'CHTEC';
export const MV_NSP_TNB_OPTION = 'DUIKA';
export const FI_NSP_TNB_OPTION = 'PCJMN';
export const MI_NSP_TNB_OPTION = 'MBRZJ';
export const NSP_OPTIONS = [MV_NSP_OPTION, FI_NSP_OPTION, MI_NSP_OPTION];
export const NSP_TRY_AND_BUY_OPTIONS = [
  MV_NSP_TNB_OPTION,
  FI_NSP_TNB_OPTION,
  MI_NSP_TNB_OPTION,
];

export const getNspOption = (product: Product) =>
  product.options?.find((value) =>
    [...NSP_OPTIONS, ...NSP_TRY_AND_BUY_OPTIONS].includes(
      value.optionCodeForDescription,
    ),
  );

export interface INspValues {
  [key: string]: INspValue;
}

export interface INspValue {
  accessNumber: string;
  value?: boolean;
  active: boolean;
  trialOption?: TrialOption;
  chargeConfiguration: ChargeConfiguration;
  plc: PlcCodes;
  installationAddress?: Location;
  mpcUuid: string;
  installationId?: string;
}

export interface INspStatistics {
  total: number;
  malwares: number;
  phishing: number;
  crypto: number;
  commands: number;
}

export interface INspPrices {
  [key: string]: Price[];
}

export const getNspInternetProducts = (products: Product[]) => {
  return products
    ? products.filter((product) => product.plc == PlcCodes.FI)
    : [];
};

export const getNspMobileProducts = (products: Product[]) => {
  return products
    ? products.filter(
        (product) => [PlcCodes.MV, PlcCodes.MI].indexOf(product.plc) > -1,
      )
    : [];
};

export const getNspPrice = (
  nspPrices: INspPrices,
  mpcUuid: string,
  index: number,
) => {
  let price = {} as Price;
  if (nspPrices) {
    const prices = nspPrices[mpcUuid];
    if (prices && prices.length > index) {
      price = prices[index];
    }
  }
  return price;
};

export const buildWebshopOrderInfo = (
  internetNspValues: INspValues,
  mobileNspValues: INspValues,
  isCease?: boolean,
) => {
  let installationPointId;
  if (internetNspValues) {
    const internetNspKeys = getNspKeys(internetNspValues);
    // get the installationId of the first internet product (there's only internet product in a pack or per address)
    if (internetNspKeys && internetNspKeys.length > 0) {
      installationPointId =
        internetNspValues[internetNspKeys[0]].installationId;
    }
  }
  const orderInfo = {
    installationPointId,
    productsInfo: buildWebshopProductsInfo(
      internetNspValues,
      mobileNspValues,
      isCease,
    ),
  };

  return orderInfo;
};

export const buildWebshopProductsInfo = (
  internetNspValues: INspValues,
  mobileNspValues: INspValues,
  isCease?: boolean,
) => {
  const webshopProductsInfo = [];
  const plcMap = buildWebshopServiceInfoList(
    internetNspValues,
    mobileNspValues,
    isCease,
  );
  for (const plc of plcMap.keys()) {
    if (plcMap.get(plc).length > 0) {
      webshopProductsInfo.push({
        plc,
        serviceInfo: plcMap.get(plc),
      });
    }
  }
  return webshopProductsInfo;
};

export const buildWebshopServiceInfoList = (
  internetNspValues: INspValues,
  mobileNspValues: INspValues,
  isCease: boolean,
) => {
  const webshopFIServiceInfoList = [];
  const webshopMVServiceInfoList = [];
  const webshopMIServiceInfoList = [];

  if (internetNspValues) {
    getNspKeys(internetNspValues).forEach((key) => {
      const value = internetNspValues[key];
      if (value) {
        if (isCease) {
          value.value = false; // force to false as we want to cease
        }
        if (value.active != value.value) {
          webshopFIServiceInfoList.push(buildWebshopServiceInfo(value));
        }
      }
    });
  }

  if (mobileNspValues) {
    getNspKeys(mobileNspValues).forEach((key, index) => {
      const value = mobileNspValues[key];
      if (value) {
        if (isCease) {
          value.value = false; // force to false as we want to cease
        }
        if (value.plc === PlcCodes.MV && value.active != value.value) {
          webshopMVServiceInfoList.push(buildWebshopServiceInfo(value));
        }
        if (value.plc === PlcCodes.MI && value.active != value.value) {
          webshopMIServiceInfoList.push(buildWebshopServiceInfo(value));
        }
      }
    });
  }
  const webshopPlcMap = new Map<PlcCodes, any[]>();
  webshopPlcMap.set(PlcCodes.FI, webshopFIServiceInfoList);
  webshopPlcMap.set(PlcCodes.MV, webshopMVServiceInfoList);
  webshopPlcMap.set(PlcCodes.MI, webshopMIServiceInfoList);

  return webshopPlcMap;
};

export const buildWebshopServiceInfo = (value: INspValue) => {
  let endOfTrialDecision: string;
  if (!value.value && value.trialOption?.endOfTrialDecision !== undefined) {
    endOfTrialDecision = value.trialOption.endOfTrialDecision === 'CTP' ? 'NCTP' : 'CTP';
  }
  return {
    serviceId: value.accessNumber,
    products: [
      {
        mpcUuid: value.mpcUuid,
        chargeCode: value.chargeConfiguration?.code,
        chargeEvent: value.chargeConfiguration?.event,
        type: 'RPO',
        action: value.value ? 'add' : 'remove',
        ...(!value.value && value.trialOption ? 
          { configuration: { endOfTrialDecision } } : {}),
      },
    ],
  };
};

export const getNspKeys = (nspValues: INspValues) => {
  return Object.keys(nspValues);
};
